import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import {
  DatabaseLayoutType,
  DatabaseView,
  databaseViewSettingsValidator,
  DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  ViewId,
} from '@databases/types/database/view';
import { getCustomProperty } from '@databases/utils/confluence/utils';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  applyViewChange,
  getDefaultView,
  getViewById,
  ViewChangeType,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { useEffect } from 'react';

export interface LayoutSettingsInterface {
  [DatabaseLayoutType.TABLE]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.CARD]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.BOARD]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.CALENDAR]: DatabaseViewSettings['layout'];
}

export const initialLayoutSettings: LayoutSettingsInterface = {
  [DatabaseLayoutType.TABLE]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.TABLE,
  },
  [DatabaseLayoutType.CARD]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.CARD,
  },
  [DatabaseLayoutType.BOARD]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.BOARD,
  },
  [DatabaseLayoutType.CALENDAR]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.CALENDAR,
  },
};

export const ViewLoader = ({
  setView,
  viewId,
  urlSavedViewId,
  unsavedViewId,
  unsavedView,
  viewChangeList,
  setUnsavedView,
  setResolvedView,
  setLayoutSettings,
}: {
  setView: (view: DatabaseView) => void;
  viewId: string | null;
  urlSavedViewId: string | null;
  unsavedViewId: string | null;
  unsavedView: DatabaseView | null;
  viewChangeList: ViewChangeType[];
  setUnsavedView: (view: DatabaseView | null) => void;
  setResolvedView: (view: DatabaseView) => void;
  setLayoutSettings: (layoutSettings: LayoutSettingsInterface) => void;
}) => {
  const { contentId } = useDatabaseContent();

  const [selectedView, savedView, databaseDefaultView] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdOptional(state, {
        contentId: contentId ?? '',
      });

      if (!database || isBrokenDatabase(database)) {
        return [undefined, undefined, undefined];
      }

      return [
        getViewById(database.views, viewId),
        getViewById(database.views, urlSavedViewId),
        getDefaultView(database),
      ];
    });

  // Loading unsaved view
  useEffect(() => {
    if (unsavedViewId) {
      getCustomProperty(contentId ?? '', unsavedViewId).then((unsavedView) => {
        if (!unsavedView) {
          return;
        }

        setUnsavedView({
          id: ViewId.UNSAVED,
          name: 'Unsaved View',
          settings: databaseViewSettingsValidator.parse(unsavedView.settings),
        });
      });
    }
  }, [contentId, setUnsavedView, unsavedViewId]);

  useEffect(() => {
    let baseView = selectedView ?? databaseDefaultView;

    if (unsavedView && viewId === ViewId.UNSAVED) {
      baseView = unsavedView;
    }

    if (baseView) {
      if (viewChangeList.length === 0) {
        setLayoutSettings({
          ...initialLayoutSettings,
          [baseView.settings.layout.type]: baseView.settings.layout,
        });
        setView(baseView);
      } else {
        const newView = viewChangeList.reduce(
          (view, viewChange) => applyViewChange(view, viewChange),
          baseView
        );

        setView(newView);
      }
    }
  }, [
    databaseDefaultView,
    selectedView,
    setLayoutSettings,
    setView,
    unsavedView,
    unsavedViewId,
    viewId,
    viewChangeList,
  ]);

  useEffect(() => {
    if (unsavedView) {
      return setResolvedView(unsavedView);
    } else if (savedView) {
      return setResolvedView(savedView);
    } else if (databaseDefaultView) {
      return setResolvedView(databaseDefaultView);
    }
  }, [databaseDefaultView, savedView, urlSavedViewId, setResolvedView, unsavedView]);

  return null;
};
